import {
  FlexBox,
  IconButton,
  Page,
  Spinner,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { webformEndpointsApi } from 'apis/webformEndpoints';
import { HookFormSwitch } from 'components/hookForm';
import { articles } from 'utils/elevio';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useNavigatePrompt } from 'utils/url/useNavigatePrompt';

import { CustomForm, CustomFormField } from '../../customForms.types';
import { useCustomForms } from '../../hooks/useCustomForms/useCustomForms';

import { EditFormSection } from './components/editFormSection/editFormSection';
import { FormEditPageTitle } from './components/formEditPageTitle';
import { WebformEndpointsSection } from './components/webformEndpointsSection/webformEndpointsSection';
import { FormValues } from './types';

function getDefaultValues(form: CustomForm | undefined): FormValues {
  return {
    feedbackAfterSubmit: form?.feedbackAfterSubmit ?? '',
    submitButtonLabel: form?.submitButtonLabel ?? '',
    formFields:
      form?.fields.map((field) => ({
        title: field.title,
        placeholder: field.placeholder,
        id: field.id,
        type: field.type,
        required: field.required,
        options: 'options' in field ? field.options : undefined,
      })) ?? [],
  };
}

export function FormsEditPage(): JSX.Element | null {
  const [form, setForm] = useState<CustomForm | undefined>(undefined);
  const { formId } = useAppPathParams('/settings/custom-forms/edit/:formId');
  const { getSingleForm, isLoading: isLoadingForms } = useCustomForms();

  const formState = useForm<FormValues>({
    mode: 'all',
    defaultValues: async () => {
      const result = await getSingleForm(formId);

      if (result.ok) {
        setForm(result.value);
        return getDefaultValues(result.value);
      }

      return getDefaultValues(undefined);
    },
  });

  useNavigatePrompt(formState.formState.isDirty);

  const formFields: CustomFormField[] | undefined = useWatch({
    control: formState.control,
    name: 'formFields',
  });

  const { webformEndpoints, isLoading, isError, isFetching, refetch } =
    webformEndpointsApi.useReadWebformEndpointsQuery(
      { formIntegrationId: formId },
      {
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          webformEndpoints: data?.data.endpoints || [],
        }),
      },
    );

  const isLoadingWebFormsApi = isLoading || isFetching;
  const formHasEndpointsAssociatedWithIt = webformEndpoints.length > 0;

  const { control } = useForm<{
    showFormsEndpointsIntegrationSettings: boolean;
  }>({
    values: {
      showFormsEndpointsIntegrationSettings: formHasEndpointsAssociatedWithIt,
    },
  });

  const { showFormsEndpointsIntegrationSettings } = useWatch({ control });

  const { hasCustomFormsV2Feature } = useFeatureFlag([
    'has-custom-forms-v2-feature',
  ]);

  function openFormsIntegrationDocumentation(): void {
    elevioClient.openArticle(
      articles.settings.customForms.webformsIntegration.documentation,
    );
  }

  if (isLoadingForms || isLoadingWebFormsApi) {
    <FlexBox flex="1" justifyContent="center" alignItems="center">
      <Spinner />
    </FlexBox>;
  }

  if (!form) {
    return null;
  }

  const hasAnyRequiredEmailFields =
    formFields?.some(
      (field) => field.type === 'email' && field.required === true,
    ) ?? false;

  const documentationButton = (
    <IconButton
      icon="question"
      variant="tertiary"
      onClick={openFormsIntegrationDocumentation}
    />
  );

  const disableFormsEndpointsSettingsSwitch =
    !hasAnyRequiredEmailFields || formHasEndpointsAssociatedWithIt;

  return (
    <FormProvider {...formState}>
      <Page>
        <Page.Head title={<FormEditPageTitle formName={form.title} />} />

        <Page.Body>
          <EditFormSection
            form={form}
            hasAnyEndpointAssociatedWithForm={webformEndpoints.length > 0}
          />

          {hasCustomFormsV2Feature && (
            <>
              <FlexBox padding="300 400" style={{ position: 'relative' }}>
                <Tooltip
                  content={
                    !hasAnyRequiredEmailFields
                      ? 'A required email field is needed to support the form endpoints'
                      : formHasEndpointsAssociatedWithIt
                      ? "Can't disable while the form has endpoints associated with it"
                      : undefined
                  }
                >
                  <HookFormSwitch
                    name="showFormsEndpointsIntegrationSettings"
                    control={control}
                    disabled={disableFormsEndpointsSettingsSwitch}
                    componentProps={{
                      disabled: disableFormsEndpointsSettingsSwitch,
                    }}
                    label={
                      <>
                        Enable webform endpoints
                        {!showFormsEndpointsIntegrationSettings &&
                          documentationButton}
                      </>
                    }
                  />
                </Tooltip>
              </FlexBox>

              {showFormsEndpointsIntegrationSettings && (
                <WebformEndpointsSection
                  formId={form.id}
                  fetchState={
                    isLoadingWebFormsApi
                      ? 'loading'
                      : isError
                      ? 'error'
                      : 'success'
                  }
                  retryFetch={refetch}
                  webformEndpoints={webformEndpoints}
                  documentationButton={documentationButton}
                />
              )}
            </>
          )}
        </Page.Body>
      </Page>
    </FormProvider>
  );
}
